import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  useClaim,
  useCoClaim,
  useCreateAppeal,
  useSettings,
  useUploadPhoto,
  useUsers,
  useWeek,
} from '../api/hooks.ts'
import { useAuth } from '../auth/AuthContext.tsx'
import { BalanceRing } from '../components/BalanceRing.tsx'
import { PhotoButton } from '../components/PhotoButton.tsx'
import { PhotoThumb } from '../components/PhotoThumb.tsx'
import { PromptDialog } from '../components/PromptDialog.tsx'
import { TaskRow } from '../components/TaskRow.tsx'
import { Banner, Button, Card, ErrorNote, Screen, SectionTitle, Spinner } from '../components/ui.tsx'
import { dayLabel, eur, shortDate, weekLabel } from '../lib/format.ts'
import type { Instance } from '../api/types.ts'

function todayIso(): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
}

export function ChildHome() {
  const { user } = useAuth()
  const params = useParams<{ userId?: string }>()
  const targetId = params.userId ?? user?.id
  const isSelf = targetId === user?.id

  const week = useWeek(params.userId)
  const settings = useSettings()
  const users = useUsers()
  const claim = useClaim()
  const upload = useUploadPhoto()
  const appeal = useCreateAppeal()
  const coClaim = useCoClaim()
  const [appealing, setAppealing] = useState<{ id: string; title: string } | null>(null)
  const [sharing, setSharing] = useState<{ id: string; title: string } | null>(null)

  const [photoError, setPhotoError] = useState<string | null>(null)

  const today = todayIso()

  /**
   * Reclamar y subir van en este orden a propósito: en un bonus abierto la
   * instancia no tiene dueño hasta que alguien gana la carrera, así que primero
   * se reclama y sólo después se adjunta la prueba. Si la reclamación falla
   * porque se adelantó el hermano, la foto ni se sube.
   */
  async function submitPhoto(id: string, file: File, alsoClaim: boolean) {
    setPhotoError(null)
    try {
      if (alsoClaim) await claim.mutateAsync({ id })
      await upload.mutateAsync({ id, file })
    } catch (error) {
      setPhotoError(error instanceof Error ? error.message : 'No se ha podido subir la foto')
    }
  }

  const groups = useMemo(() => {
    const all = week.data?.instances ?? []
    const deductible = all.filter((i) => i.kind === 'DEDUCTIBLE')
    return {
      todayTasks: deductible.filter((i) => i.dueDate === today),
      laterTasks: deductible.filter((i) => i.dueDate > today),
      warnings: deductible.filter((i) => i.status === 'OK' && i.warningCount > 0),
      deductions: deductible.filter((i) => i.status === 'NOT_DONE'),
      available: all.filter((i) => i.kind === 'BONUS' && i.status === 'AVAILABLE'),
      pending: all.filter((i) => i.kind === 'BONUS' && i.status === 'CLAIMED'),
      approved: all.filter((i) => i.kind === 'BONUS' && i.status === 'APPROVED'),
    }
  }, [week.data, today])

  if (week.isLoading || settings.isLoading) return <Spinner />
  if (week.error) return <Screen><ErrorNote error={week.error} /></Screen>
  if (!week.data || !settings.data) return <Spinner />

  const { breakdown, week: w } = week.data
  const threshold = settings.data.warningThreshold
  const name = users.data?.find((u) => u.id === targetId)?.name ?? user?.name ?? ''

  // Cupo de bonus que queda esta semana. Se calcula aqui para poder avisar
  // ANTES de que el hijo se ponga a trabajar (principio 4).
  const bonusRemaining =
    breakdown.bonusCapCents === null
      ? Infinity
      : Math.max(0, breakdown.bonusCapCents - breakdown.bonusGrossCents)

  const minGuaranteed =
    breakdown.deductionCapCents === null ? null : w.baseAmountCents - breakdown.deductionCapCents

  return (
    <Screen>
      <header className="mb-2">
        <h1 className="text-xl font-bold tracking-tight">
          {isSelf ? `Hola, ${name}` : name}
        </h1>
        <p className="text-sm text-[var(--color-muted)]">
          {weekLabel(w.weekStart, w.weekEnd)}
          {w.status !== 'OPEN' && ' · semana cerrada'}
        </p>
      </header>

      <BalanceRing
        payoutCents={breakdown.payoutCents}
        baseAmountCents={w.baseAmountCents}
        deductionsCents={breakdown.deductionsAppliedCents}
        bonusCents={breakdown.bonusAppliedCents}
        minGuaranteedCents={minGuaranteed}
        capReached={breakdown.deductionCapReached}
      />

      <div className="mt-3 text-center">
        <Link
          to={`/semana/${w.id}/desglose`}
          className="text-sm font-medium text-[var(--color-brand)]"
        >
          Ver el desglose céntimo a céntimo →
        </Link>
      </div>

      {/* Los avisos van arriba del todo y como banner persistente: una push se
          descarta o se pierde, y el aviso tiene que verse igualmente. */}
      {groups.warnings.length > 0 && (
        <div className="mt-5">
          {groups.warnings.map((i) => (
            <Banner key={i.id} tone="warn" title={`⚠️ Aviso ${i.warningCount}/${threshold} — ${i.title}`}>
              {i.warningCount + 1 >= threshold
                ? `Un aviso más y se marca como no hecha: perderías ${eur(i.valueCents)}.`
                : `Si llega a ${threshold} avisos, pierdes ${eur(i.valueCents)}.`}
            </Banner>
          ))}
        </div>
      )}

      {groups.deductions.length > 0 && (
        <>
          {/* Lo que ha costado dinero, separado y con su importe: es la
              información que el hijo necesita para poder rebatirla. */}
          <SectionTitle>Descuentos de esta semana</SectionTitle>
          <Card className="flex flex-col gap-3">
            {groups.deductions.map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[15px] break-words">{i.title}</p>
                  {/* El motivo va aquí y no sólo en el desglose: es lo primero
                      que se mira cuando falta dinero, y un descuento que no se
                      explica es el que acaba discutido en la cena. */}
                  {i.note && <p className="text-sm break-words">«{i.note}»</p>}
                  <p className="text-xs text-[var(--color-muted)]">
                    {shortDate(i.dueDate)}
                    {i.appealStatus === 'OPEN' && ' · alegación enviada'}
                    {i.appealStatus === 'REJECTED' && ' · alegación rechazada'}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="text-sm tabular-nums text-[var(--color-bad)]">
                    −{eur(i.valueCents)}
                  </span>
                  {isSelf && w.status === 'OPEN' && i.appealStatus === null && (
                    <Button
                      variant="secondary"
                      onClick={() => setAppealing({ id: i.id, title: i.title })}
                    >
                      No estoy de acuerdo
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </Card>
          {appeal.error != null && (
            <div className="mt-2">
              <ErrorNote error={appeal.error} />
            </div>
          )}
        </>
      )}

      <SectionTitle>Hoy · {dayLabel(today)}</SectionTitle>
      <Card>
        {groups.todayTasks.length === 0 ? (
          <p className="py-2 text-sm text-[var(--color-muted)]">No hay tareas para hoy.</p>
        ) : (
          groups.todayTasks.map((i) => (
            <TaskRow key={i.id} instance={i} warningThreshold={threshold} />
          ))
        )}
      </Card>

      {isSelf && (groups.available.length > 0 || groups.pending.length > 0) && (
        <>
          <SectionTitle>Bonus</SectionTitle>
          <Card className="flex flex-col gap-3">
            {groups.pending.map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  {i.hasPhoto && <PhotoThumb instanceId={i.id} alt={i.title} />}
                  <div className="min-w-0">
                    <p className="text-[15px] break-words">{i.title}</p>
                    <p className="text-xs text-[var(--color-muted)]">Pendiente de aprobación</p>
                  </div>
                </div>
                {i.requiresPhoto && !i.hasPhoto ? (
                  // Sin foto el admin no puede aprobarla, así que la app lo dice
                  // aquí en vez de dejar la reclamación en el limbo.
                  <PhotoButton
                    variant="secondary"
                    className="shrink-0"
                    ariaLabel={`Añadir foto de ${i.title}`}
                    disabled={upload.isPending}
                    onFile={(file) => void submitPhoto(i.id, file, false)}
                  >
                    Falta la foto
                  </PhotoButton>
                ) : (
                  <span className="shrink-0 text-sm text-[var(--color-muted)]">
                    +{eur(i.valueCents)}
                  </span>
                )}
              </div>
            ))}

            {groups.available.map((i) => {
              const blocked = bonusRemaining <= 0
              const partial = !blocked && i.valueCents > bonusRemaining
              return (
                <div key={i.id} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[15px] break-words">{i.title}</p>
                    {blocked ? (
                      <p className="text-xs text-[var(--color-warn)]">Cupo semanal alcanzado</p>
                    ) : partial ? (
                      <p className="text-xs text-[var(--color-warn)]">
                        Solo se te abonarán {eur(bonusRemaining)} por el cupo
                      </p>
                    ) : (
                      <p className="text-xs text-[var(--color-good)]">
                        +{eur(i.valueCents)}
                        {i.requiresPhoto && ' · pide foto'}
                      </p>
                    )}
                  </div>
                  {i.requiresPhoto ? (
                    <PhotoButton
                      variant={blocked ? 'secondary' : 'primary'}
                      className="shrink-0"
                      ariaLabel={`Elegir foto de ${i.title}`}
                      disabled={blocked || claim.isPending || upload.isPending}
                      onFile={(file) => void submitPhoto(i.id, file, true)}
                    >
                      📷 Lo he hecho
                    </PhotoButton>
                  ) : (
                    <Button
                      variant={blocked ? 'secondary' : 'primary'}
                      disabled={blocked || claim.isPending}
                      onClick={() => claim.mutate({ id: i.id })}
                      className="shrink-0"
                    >
                      Lo he hecho
                    </Button>
                  )}
                </div>
              )
            })}
          </Card>

          {photoError != null && <div className="mt-2"><ErrorNote error={photoError} /></div>}
          {claim.error != null && <div className="mt-2"><ErrorNote error={claim.error} /></div>}
        </>
      )}

      {/* Lo que se llevó el hermano de un bonus que este hijo también podía
          hacer. Si participó, aquí lo dice; si no, sólo es información. */}
      {isSelf && (week.data.sharedBonus ?? []).length > 0 && (
        <>
          <SectionTitle>Se lo ha llevado tu hermano</SectionTitle>
          <Card className="flex flex-col gap-3">
            {week.data.sharedBonus.map((b) => (
              <div key={b.instanceId} className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[15px] break-words">{b.title}</p>
                  <p className="text-xs text-[var(--color-muted)]">
                    {b.claimedByName} · {eur(b.valueCents)}
                    {b.myCoClaimStatus === 'OPEN' && ' · has pedido tu parte'}
                    {b.myCoClaimStatus === 'ACCEPTED' && ' · se repartió'}
                    {b.myCoClaimStatus === 'REJECTED' && ' · se quedó entero'}
                  </p>
                </div>
                {b.myCoClaimStatus === null && (
                  <Button
                    variant="secondary"
                    className="shrink-0"
                    disabled={coClaim.isPending}
                    onClick={() => setSharing({ id: b.instanceId, title: b.title })}
                  >
                    Yo también
                  </Button>
                )}
              </div>
            ))}
          </Card>
          {coClaim.error != null && <div className="mt-2"><ErrorNote error={coClaim.error} /></div>}
        </>
      )}

      {groups.laterTasks.length > 0 && (
        <>
          <SectionTitle>Resto de la semana</SectionTitle>
          <Card>
            {groups.laterTasks.map((i) => (
              <TaskRow key={i.id} instance={i} warningThreshold={threshold} />
            ))}
          </Card>
        </>
      )}
      <PromptDialog
        open={sharing !== null}
        title="¿Qué hiciste tú?"
        description={
          sharing
            ? `Se lo contarás a quien apruebe «${sharing.title}», que decidirá si se reparte a medias.`
            : undefined
        }
        placeholder="Yo lavé la mitad del coche…"
        confirmLabel="Pedir mi parte"
        busy={coClaim.isPending}
        onCancel={() => setSharing(null)}
        onConfirm={(message) => {
          if (!sharing) return
          coClaim.mutate(
            { id: sharing.id, body: { message } },
            { onSuccess: () => setSharing(null) },
          )
        }}
      />
      <PromptDialog
        open={appealing !== null}
        title="¿Por qué no estás de acuerdo?"
        description={
          appealing
            ? `Se enviará a quien haya marcado «${appealing.title}» como no hecha.`
            : undefined
        }
        placeholder="Explica qué pasó…"
        confirmLabel="Enviar alegación"
        busy={appeal.isPending}
        onCancel={() => setAppealing(null)}
        onConfirm={(message) => {
          if (!appealing) return
          appeal.mutate(
            { id: appealing.id, body: { message } },
            { onSuccess: () => setAppealing(null) },
          )
        }}
      />
    </Screen>
  )
}

export type { Instance }
